import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface DeviceActivityChartProps {
  apiBaseUrl: string;
}

interface ActivityStats {
  onlineDevices: number;
  activeDevices: number;
  offlineDevices: number;
  totalDevices: number;
}

interface HistoricalDataPoint {
  date: string;
  onlineDevices: number;
  activeDevices: number;
  offlineDevices: number;
  totalDevices: number;
  // Percentage fields (will be calculated)
  onlineDevicesPercent?: number;
  activeDevicesPercent?: number;
  offlineDevicesPercent?: number;
}

type TimeRange = {
  value: number;
  label: string;
};

type DisplayMode = 'numbers' | 'percentages';

const DeviceActivityChart: React.FC<DeviceActivityChartProps> = ({ apiBaseUrl }) => {
  const [currentStats, setCurrentStats] = useState<ActivityStats | null>(null);
  const [historicalData, setHistoricalData] = useState<HistoricalDataPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRange, setSelectedRange] = useState<TimeRange>({ value: 30, label: '1 Month' });
  const [displayMode, setDisplayMode] = useState<DisplayMode>('numbers');

  // Define available time ranges
  const timeRanges: TimeRange[] = [
    { value: 7, label: '1 Week' },
    { value: 30, label: '1 Month' },
    { value: 90, label: '3 Months' },
    { value: 180, label: '6 Months' },
    { value: 365, label: '1 Year' },
    { value: 0, label: 'All Time' },
  ];

  useEffect(() => {
    // Fetch current stats when component mounts
    fetchCurrentStats();
    
    // Fetch historical data
    fetchHistoricalData();
  }, []);

  useEffect(() => {
    // Refetch historical data when time range changes
    fetchHistoricalData();
  }, [selectedRange]);

  const fetchCurrentStats = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/devices/activity-metrics`, {
        withCredentials: true
      });
      
      const { totalDevices, onlineDevices, activeDevices, offlineDevices } = response.data;
      
      setCurrentStats({
        onlineDevices,
        activeDevices,
        offlineDevices,
        totalDevices
      });
    } catch (error: any) {
      console.error('Error fetching device activity statistics:', error);
      
      // Use mock data for the demo
      setCurrentStats({
        onlineDevices: 1245,
        activeDevices: 4002,
        offlineDevices: 47,
        totalDevices: 5294
      });
    }
  };

  const fetchHistoricalData = async () => {
    setLoading(true);
    try {
      const params = selectedRange.value > 0 ? { days: selectedRange.value } : {};
      
      const response = await axios.get(`${apiBaseUrl}/devices/activity-metrics/history`, {
        params,
        withCredentials: true
      });
      
      // Process and format the historical data
      const formattedData = response.data.history.map((item: any) => {
        const totalDevices = item.totalDevices || 1; // Avoid division by zero
        
        // Calculate percentages
        const onlineDevicesPercent = (item.onlineDevices / totalDevices) * 100;
        const activeDevicesPercent = (item.activeDevices / totalDevices) * 100;
        const offlineDevicesPercent = (item.offlineDevices / totalDevices) * 100;
        
        return {
          date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          onlineDevices: item.onlineDevices,
          activeDevices: item.activeDevices,
          offlineDevices: item.offlineDevices,
          totalDevices: totalDevices,
          // Add percentage fields
          onlineDevicesPercent,
          activeDevicesPercent,
          offlineDevicesPercent
        };
      });
      
      setHistoricalData(formattedData);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching historical activity data:', error);
      setError('Failed to fetch historical activity data');
      
      // Generate mock historical data for demo
      const mockData = generateMockHistoricalData();
      setHistoricalData(mockData);
    } finally {
      setLoading(false);
    }
  };

  const generateMockHistoricalData = (): HistoricalDataPoint[] => {
    const data: HistoricalDataPoint[] = [];
    const now = new Date();
    const days = selectedRange.value > 0 ? selectedRange.value : 365;
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(now.getDate() - i);
      const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      // Base values with some random fluctuation
      const onlineBase = 900 + i * 0.5;
      const activeBase = 2100 + i * 0.4;
      const offlineBase = 2400 - i * 0.8;
      
      const onlineDevices = Math.max(0, onlineBase + Math.floor(Math.random() * 50 - 25));
      const activeDevices = Math.max(0, activeBase + Math.floor(Math.random() * 40 - 20));
      const offlineDevices = Math.max(0, offlineBase + Math.floor(Math.random() * 30 - 15));
      const totalDevices = 5400 + Math.floor(i / 10);
      
      // Calculate percentages
      const onlineDevicesPercent = (onlineDevices / totalDevices) * 100;
      const activeDevicesPercent = (activeDevices / totalDevices) * 100;
      const offlineDevicesPercent = (offlineDevices / totalDevices) * 100;
      
      data.push({
        date: formattedDate,
        onlineDevices,
        activeDevices,
        offlineDevices,
        totalDevices,
        onlineDevicesPercent,
        activeDevicesPercent,
        offlineDevicesPercent
      });
    }
    
    return data;
  };

  const handleRangeChange = (range: TimeRange) => {
    setSelectedRange(range);
  };

  const toggleDisplayMode = () => {
    setDisplayMode(prev => prev === 'numbers' ? 'percentages' : 'numbers');
  };

  const getTickInterval = () => {
    if (historicalData.length > 60) return Math.floor(historicalData.length / 15);
    if (historicalData.length > 30) return Math.floor(historicalData.length / 10);
    return 0; // Show all ticks for smaller datasets
  };

  // Get current active data keys based on display mode
  const getActiveKeys = () => {
    return {
      online: displayMode === 'numbers' ? 'onlineDevices' : 'onlineDevicesPercent',
      active: displayMode === 'numbers' ? 'activeDevices' : 'activeDevicesPercent',
      offline: displayMode === 'numbers' ? 'offlineDevices' : 'offlineDevicesPercent'
    };
  };

  // Custom tooltip formatter
  const tooltipFormatter = (value: number, name: string, props: any) => {
    const totalDevices = props.payload?.totalDevices || 0;
    
    if (displayMode === 'numbers') {
      // For numbers mode, show both actual count and percentage
      const percentage = totalDevices > 0 ? ((Number(value) / totalDevices) * 100).toFixed(1) : 0;
      return [`${Number(value).toLocaleString()} devices (${percentage}%)`, getLabelForKey(name)];
    } else {
      // For percentage mode, show percentage and actual count
      const actualValueKey = name.replace('Percent', '');
      const actualValue = props.payload?.[actualValueKey] || 0;
      return [`${value.toFixed(1)}% (${Number(actualValue).toLocaleString()} devices)`, getLabelForKey(name)];
    }
  };

  // Helper to get display labels for keys
  const getLabelForKey = (key: string) => {
    if (key === 'onlineDevices' || key === 'onlineDevicesPercent') return 'Online (3 days)';
    if (key === 'activeDevices' || key === 'activeDevicesPercent') return 'Active (month)';
    if (key === 'offlineDevices' || key === 'offlineDevicesPercent') return 'Offline';
    return key;
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">Device Activity</h5>
          <div className="text-muted small">Time of last check-in over time</div>
        </div>
        <div className="card-body text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && historicalData.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">Device Activity</h5>
        </div>
        <div className="card-body">
          <div className="alert alert-danger">{error}</div>
        </div>
      </div>
    );
  }

  const activeKeys = getActiveKeys();

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">Device Activity</h5>
        <div className="text-muted small">Time of last check-in over time</div>
      </div>
      
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="btn-group">
            {timeRanges.map((range) => (
              <button
                key={range.value}
                className={`btn btn-sm ${selectedRange.value === range.value ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => handleRangeChange(range)}
              >
                {range.label}
              </button>
            ))}
          </div>
          
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={toggleDisplayMode}
          >
            Show {displayMode === 'numbers' ? 'Percentages' : 'Numbers'}
          </button>
        </div>
        
        <div style={{ width: '100%', height: 400 }}>
          <ResponsiveContainer>
            <LineChart
              data={historicalData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                interval={getTickInterval()}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis
                domain={displayMode === 'numbers' ? [0, 'dataMax + 100'] : [0, 100]}
                tickFormatter={value => displayMode === 'numbers' ? value.toLocaleString() : `${value}%`}
              />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              <Line
                type="monotone"
                dataKey={activeKeys.online}
                name="Online (3 days)"
                stroke="#28a745"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey={activeKeys.active}
                name="Active (month)"
                stroke="#17a2b8"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey={activeKeys.offline}
                name="Offline"
                stroke="#dc3545"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      <div className="card-footer bg-light d-flex justify-content-between align-items-center">
        <span className="small text-muted">Updated: {new Date().toLocaleTimeString()}</span>
        <button 
          className="btn btn-sm btn-outline-secondary" 
          onClick={fetchHistoricalData}
        >
          Refresh
        </button>
      </div>
    </div>
  );
};

export default DeviceActivityChart; 